import color from 'atoms/colors/colors';
import shadow from 'atoms/shadows/shadows';
import { padding } from 'atoms/spacing/spacing';
import { fontSize, fontWeight } from 'atoms/typography/typography';

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xsmall: true;
    xlarge: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    primary: true;
    secondary_gray: true;
    secondary_color: true;
    tertiary_gray: true;
    tertiary_color: true;
    link_gray: true;
    link_color: true;
    link_dark_mode: true;
    dark_mode: true;
    rta: true;
    transparent_white: true;
    transparent_color: true;
    gray_border: true;
  }
}

const themeOverrides = {
  root: {
    minWidth: 'fit-content',
    textTransform: 'capitalize',
    fontWeight: fontWeight.semiBold,
    borderRadius: '8px',
    fontSize: fontSize.textSm,
    paddingLeft: padding[32],
    paddingRight: padding[32],
    boxShadow: shadow.xs,
    alignItems: 'center',
    '&:hover': {
      boxShadow: shadow.xs,
    },
    '&:focus': {
      boxShadow: `${shadow.xs}, 0px 0px 0px 4px #F2F4F7`,
    },
    '.MuiButton-startIcon': {
      margin: 0,
    },
  },
};

export const paletteVariants = {
  primary: {
    main: color.lightning[600],
  },
  secondary_gray: {
    main: color.foam[50],
  },
  secondary_color: {
    main: color.bubbles[50],
  },
  tertiary_gray: {
    main: color.storm[900],
  },
  tertiary_color: {
    main: color.highSeas[400],
  },
  link_gray: {
    main: color.storm[900],
  },
  link_color: {
    main: color.wave[900],
  },
  link_dark_mode: {
    main: color.common.white,
  },
  dark_mode: {
    main: color.common.white,
  },
  rta: {
    main: color.common.splatBlue,
  },
  transparent_white: {
    main: color.common.white,
  },
  transparent_color: {
    main: color.highSeas[400],
  },
  gray_border: {
    main: color.foam[50],
  },
};

const sizeVariants = [
  {
    props: { size: 'xsmall' },
    style: {
      paddingTop: padding[8],
      paddingBottom: padding[8],
      paddingLeft: '14px',
      paddingRight: '14px',
      lineHeight: '20px',
      margin: '0 8px',
      borderRadius: '18px',
      height: '36px',
      '&.icon-button': {
        padding: 0,
        width: '36px!important',
      },
    },
  },
  {
    props: { size: 'small' },
    style: {
      paddingTop: '10px',
      paddingBottom: '10px',
      lineHeight: '20px',
      borderRadius: '20px',
      margin: '0 8px',
      height: '40px',
      '&.icon-button': {
        padding: 0,
        width: '40px!important',
      },
    },
  },
  {
    props: { size: 'medium' },
    style: {
      fontSize: fontSize.textMd,
      lineHeight: '24px',
      padding: '10px 18px',
      borderRadius: '22px',
      height: '44px',
      margin: '0 8px',
      '&.icon-button': {
        padding: 0,
        width: '44px!important',
      },
    },
  },
  {
    props: { size: 'large' },
    style: {
      fontSize: fontSize.textMd,
      lineHeight: '24px',
      padding: '12px 20px',
      borderRadius: '24px',
      height: '48px',
      margin: '0 8px',
      '&.icon-button': {
        padding: 0,
        width: '48px!important',
      },
    },
  },
  {
    props: { size: 'xlarge' },
    style: {
      fontSize: fontSize.textLg,
      lineHeight: '28px',
      padding: '16px 28px',
      borderRadius: '30px',
      height: '56px',
      margin: '0 8px',
      '&.icon-button': {
        padding: 0,
        width: '56px!important',
      },
    },
  },
];

const colorVariants = [
  {
    props: { color: 'primary' },
    style: {
      backgroundColor: color.lightning[600],
      color: color.storm[900],
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.lightning[700],
      },
      '&:focus': {
        backgroundColor: color.lightning[700],
        boxShadow: `0 0 0 4px ${color.lightning[100]}`,
      },
      '&[disabled]': {
        backgroundColor: color.lightning[100],
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'secondary_gray' },
    style: {
      backgroundColor: 'transparent',
      color: color.storm[900],
      border: `1px solid ${color.storm[900]}`,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.common.white,
      },
      '&:focus': {
        backgroundColor: color.foam[100],
        boxShadow: `${shadow.xs}, 0px 1px 2px rgba(61, 95, 130, 0.05)`,
      },
      '&[disabled]': {
        borderColor: color.storm[300],
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'secondary_color' },
    style: {
      backgroundColor: color.bubbles[50],
      color: color.storm[900],
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.bubbles[100],
      },
      '&:focus': {
        backgroundColor: color.bubbles[50],
        boxShadow: `0 0 0 4px ${color.bubbles[25]}`,
      },
      '&[disabled]': {
        backgroundColor: color.bubbles[25],
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'tertiary_gray' },
    style: {
      backgroundColor: color.storm[900],
      color: color.common.white,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.storm[700],
      },
      '&:focus': {
        backgroundColor: color.storm[900],
        boxShadow: `0 0 0 4px ${color.storm[50]}`,
      },
      '&[disabled]': {
        backgroundColor: color.storm[25],
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'tertiary_color' },
    style: {
      backgroundColor: color.highSeas[400],
      color: color.storm[900],
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.highSeas[600],
      },
      '&:focus': {
        backgroundColor: color.highSeas[400],
        boxShadow: `0 0 0 4px ${color.highSeas[100]}`,
      },
      '&[disabled]': {
        backgroundColor: color.highSeas[25],
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'link_gray' },
    style: {
      color: color.storm[900],
      boxShadow: 'unset',
      background: 'transparent',
      padding: '0',
      height: 'unset',
      '&:hover': {
        color: color.storm[600],
        background: 'transparent',
        boxShadow: 'unset',
      },
      '&:focus': {
        color: color.storm[900],
        background: 'transparent',
        boxShadow: 'unset',
      },
      '&[disabled]': {
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'link_color' },
    style: {
      color: color.wave[900],
      boxShadow: 'unset',
      background: 'transparent',
      padding: '0',
      height: 'unset',
      '&:hover': {
        color: color.wave[700],
        background: 'transparent',
        boxShadow: 'unset',
      },
      '&:focus': {
        color: color.wave[800],
        background: 'transparent',
        boxShadow: 'unset',
      },
      '&[disabled]': {
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'dark_mode' },
    style: {
      color: color.foam[50],
      borderWidth: '1px',
      border: `1px solid ${color.foam[50]}`,
      backgroundColor: 'transparent',
      textTransform: 'uppercase',
      '&:hover': {
        color: color.common.white,
        borderColor: color.common.white,
      },
      '&:focus': {
        color: color.foam[400],
        borderColor: color.foam[400],
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
      },
      '&[disabled]': {
        color: color.storm[300],
        borderColor: color.storm[300],
      },
    },
  },
  {
    props: { color: 'link_dark_mode' },
    style: {
      color: color.foam[50],
      border: 'unset!important',
      background: 'unset!important',
      padding: '0',
      height: 'unset',
      '&:hover': {
        color: color.common.white,
        boxShadow: 'unset',
      },
      '&:focus': {
        color: color.foam[400],
        boxShadow: 'unset',
      },
      '&[disabled]': {
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'transparent_white' },
    style: {
      color: color.common.white,
      backgroundColor: color.common.transparent,
      border: `1px solid ${color.common.white}`,
      textTransform: 'uppercase',
      '&:hover': {
        color: color.storm[900],
        backgroundColor: color.common.white,
      },
      '&:focus': {
        boxShadow: 'unset',
      },
      '&[disabled]': {
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'transparent_color' },
    style: {
      color: color.storm[900],
      backgroundColor: color.common.transparent,
      border: `1px solid ${color.highSeas[400]}`,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
      },
      '&:focus': {
        boxShadow: 'unset',
      },
      '&[disabled]': {
        color: color.storm[300],
      },
    },
  },
  {
    props: { color: 'gray_border' },
    style: {
      backgroundColor: color.foam[50],
      color: color.storm[900],
      border: `1px solid ${color.storm[900]}`,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: color.common.white,
      },
      '&:focus': {
        backgroundColor: color.foam[100],
        boxShadow: `${shadow.xs}, 0px 1px 2px rgba(61, 95, 130, 0.05)`,
      },
      '&[disabled]': {
        borderColor: color.storm[300],
        color: color.storm[300],
      },
    },
  },
];

const socialVariants = [
  {
    props: { variant: 'contained' },
    style: {
      '&.social-google': {
        background: color.common.white,
        border: `1px solid ${color.storm[300]}`,
        '&:hover': {
          background: color.storm[50],
        },
      },
      '&.social-facebook': {
        background: '#1877F2',
        color: color.common.white,
        '&:hover': {
          background: '#0C63D4',
        },
        '.MuiButton-startIcon': {
          color: color.common.white,
          svg: {
            path: {
              fill: color.common.white,
            },
          },
        },
      },
      '&.social-apple': {
        background: color.common.black,
        color: color.common.white,
        '&:hover': {
          background: color.common.black,
        },
        '.MuiButton-startIcon': {
          color: color.common.white,
          svg: {
            path: {
              fill: color.common.white,
            },
          },
        },
      },
      '&.social-twitter': {
        background: '#1DA1F2',
        color: color.common.white,
        '&:hover': {
          background: '#0C8BD9',
        },
        '.MuiButton-startIcon': {
          color: color.common.white,
          svg: {
            path: {
              fill: color.common.white,
            },
          },
        },
      },
      '&.social-figma': {
        background: color.common.black,
        color: color.common.white,
        '&:hover': {
          background: color.common.black,
        },
      },
      '&.social-dribbble': {
        background: '#EA4C89',
        color: color.common.white,
        '&:hover': {
          background: '#E62872',
        },
      },
    },
  },
  {
    props: { variant: 'outlined' },
    style: {
      background: color.common.white,
      border: `1px solid ${color.storm[300]}`,
      color: color.storm[700],
      '&:hover': {
        background: color.storm[50],
        border: `1px solid ${color.storm[300]}`,
      },
      '&.social-apple': {
        '.MuiButton-startIcon': {
          color: color.common.black,
          svg: {
            path: {
              fill: color.common.black,
            },
          },
        },
      },
      '&.is-gray': {
        '.MuiButton-startIcon': {
          color: color.storm[400],
          svg: {
            path: {
              fill: color.storm[400],
            },
          },
        },
      },
    },
  },
];

export const lightButton = {
  MuiButton: {
    styleOverrides: themeOverrides,
    variants: [...sizeVariants, ...colorVariants, ...socialVariants],
  },
};

export const darkButton = {
  MuiButton: {
    styleOverrides: themeOverrides,
    variants: [...sizeVariants, ...colorVariants, ...socialVariants],
  },
};
